import { useMemo, useState } from 'react';
import { EmptyState, FilterChipBar } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { useReportCommentMutations } from '../hooks/use-report-comment-mutations';
import { useReportComments } from '../hooks/use-report-comments';
import { CommentSectionOption } from '../types';
import { CommentFilter, getCommentSectionLabel, getCommentsForReport } from '../utils';
import { CommentThread } from './CommentThread';

interface AllCommentsModalContentProps {
    reportId?: string | null;
    sectionOptions: CommentSectionOption[];
}

export function AllCommentsModalContent({
    reportId,
    sectionOptions,
}: AllCommentsModalContentProps) {
    const { user } = useAuth();
    const commentsEnabled = Boolean(reportId);

    const [filter, setFilter] = useState<CommentFilter>('active');

    const { comments, isLoading } = useReportComments(reportId);
    const { replyToCommentMutation, updateCommentStatusMutation, deleteCommentMutation } =
        useReportCommentMutations(reportId);

    const sectionTitlesById = useMemo(
        () =>
            sectionOptions.reduce<Record<string, string>>((accumulator, section) => {
                accumulator[section.id] = section.title;
                return accumulator;
            }, {}),
        [sectionOptions],
    );

    const reportComments = useMemo(
        () => getCommentsForReport(comments, filter),
        [comments, filter],
    );

    const commentFilterOptions = useMemo(
        () => [
            {
                value: 'active',
                label: 'Abertos',
                count: getCommentsForReport(comments, 'active').length,
            },
            {
                value: 'completed',
                label: 'Resolvidos',
                count: getCommentsForReport(comments, 'completed').length,
            },
            {
                value: 'all',
                label: 'Todos',
                count: getCommentsForReport(comments, 'all').length,
            },
        ],
        [comments],
    );

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-surface/70">
            <div className="shrink-0 space-y-4 border-b border-border/70 px-6 py-5">
                <FilterChipBar
                    ariaLabel="Filtrar comentários por status"
                    value={filter}
                    onChange={(value) => setFilter(value as CommentFilter)}
                    options={commentFilterOptions}
                />
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 py-5">
                {!commentsEnabled ? (
                    <Alert variant="warning" className="shrink-0">
                        Salve o laudo primeiro para visualizar e complementar comentários.
                    </Alert>
                ) : isLoading ? (
                    <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
                        Carregando comentários...
                    </div>
                ) : reportComments.length === 0 ? (
                    <EmptyState
                        title={
                            filter === 'completed'
                                ? 'Nenhum comentário resolvido'
                                : filter === 'active'
                                  ? 'Nenhum comentário aberto'
                                  : 'Nenhum comentário ainda'
                        }
                        description={
                            filter === 'completed'
                                ? 'Comentários resolvidos deste laudo aparecerão aqui.'
                                : filter === 'active'
                                  ? 'Quando houver comentários em aberto neste laudo, eles aparecerão aqui.'
                                  : 'Adicione um comentário no laudo para começar a conversa com sua equipe.'
                        }
                    />
                ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto pr-2">
                        <div className="space-y-4">
                            {reportComments.map((comment) => (
                                <CommentThread
                                    key={comment.id}
                                    comment={comment}
                                    sectionLabel={getCommentSectionLabel(
                                        comment.context.section_type,
                                        sectionTitlesById,
                                    )}
                                    currentUserId={user?.id}
                                    currentUserName={user?.full_name}
                                    isReplyPending={
                                        replyToCommentMutation.isPending &&
                                        replyToCommentMutation.variables?.commentId === comment.id
                                    }
                                    isStatusPending={
                                        updateCommentStatusMutation.isPending &&
                                        updateCommentStatusMutation.variables?.commentId ===
                                            comment.id
                                    }
                                    isDeletePending={
                                        deleteCommentMutation.isPending &&
                                        deleteCommentMutation.variables === comment.id
                                    }
                                    onReply={async (commentId, content) => {
                                        await replyToCommentMutation.mutateAsync({
                                            commentId,
                                            payload: { content },
                                        });
                                    }}
                                    onToggleStatus={async (commentId, nextStatus) => {
                                        await updateCommentStatusMutation.mutateAsync({
                                            commentId,
                                            status: nextStatus,
                                        });
                                    }}
                                    onDelete={async (commentId) => {
                                        await deleteCommentMutation.mutateAsync(commentId);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
